/**
 * JWT token storage service with platform-aware storage.
 *
 * Uses Expo SecureStore on native platforms (iOS/Android)
 * Uses localStorage fallback for web platform
 */

import * as SecureStore from 'expo-secure-store';
import { Platform } from 'react-native';

const TOKEN_KEY = 'user_auth_token';
const REFRESH_TOKEN_KEY = 'user_refresh_token';

// Check if running on web
const isWeb = Platform.OS === 'web';

/**
 * Web storage fallback using localStorage
 */
const webStorage = {
  async getItem(key: string): Promise<string | null> {
    try {
      return localStorage.getItem(key) || null;
    } catch {
      return null;
    }
  },
  async setItem(key: string, value: string): Promise<void> {
    try {
      localStorage.setItem(key, value);
    } catch (e) {
      console.error('Error saving to localStorage:', e);
    }
  },
  async deleteItem(key: string): Promise<void> {
    try {
      localStorage.removeItem(key);
    } catch (e) {
      console.error('Error removing from localStorage:', e);
    }
  },
};

/**
 * Store the authentication token securely.
 */
export async function storeToken(token: string): Promise<void> {
  if (isWeb) {
    await webStorage.setItem(TOKEN_KEY, token);
  } else {
    await SecureStore.setItemAsync(TOKEN_KEY, token);
  }
}

/**
 * Retrieve the stored authentication token.
 */
export async function getToken(): Promise<string | null> {
  if (isWeb) {
    return await webStorage.getItem(TOKEN_KEY);
  } else {
    return await SecureStore.getItemAsync(TOKEN_KEY);
  }
}

/**
 * Remove the stored authentication token (logout).
 */
export async function removeToken(): Promise<void> {
  if (isWeb) {
    await webStorage.deleteItem(TOKEN_KEY);
  } else {
    await SecureStore.deleteItemAsync(TOKEN_KEY);
  }
}

/**
 * Store a refresh token securely.
 */
export async function storeRefreshToken(token: string): Promise<void> {
  if (isWeb) {
    await webStorage.setItem(REFRESH_TOKEN_KEY, token);
  } else {
    await SecureStore.setItemAsync(REFRESH_TOKEN_KEY, token);
  }
}

/**
 * Retrieve the stored refresh token.
 */
export async function getRefreshToken(): Promise<string | null> {
  if (isWeb) {
    return await webStorage.getItem(REFRESH_TOKEN_KEY);
  } else {
    return await SecureStore.getItemAsync(REFRESH_TOKEN_KEY);
  }
}

/**
 * Remove the stored refresh token.
 */
export async function removeRefreshToken(): Promise<void> {
  if (isWeb) {
    await webStorage.deleteItem(REFRESH_TOKEN_KEY);
  } else {
    await SecureStore.deleteItemAsync(REFRESH_TOKEN_KEY);
  }
}

/**
 * Clear all authentication tokens (complete logout).
 */
export async function clearAllTokens(): Promise<void> {
  await Promise.all([removeToken(), removeRefreshToken()]);
}

/**
 * Check if user is authenticated (has a valid token).
 */
export async function isAuthenticated(): Promise<boolean> {
  const token = await getToken();
  return token !== null && token.length > 0;
}
